import { toZonedTime } from 'date-fns-tz';
import { parse } from 'date-fns';

const ARGENTINA_TIMEZONE = 'America/Argentina/Buenos_Aires';
const BUSINESS_START_HOUR = 9;
const BUSINESS_END_HOUR = 19;

export interface ConvertedBusinessHours {
  startTime: string;
  endTime: string;
  visitorTimezone: string;
  visitorTimezoneShort: string;
  spansMultipleDays: boolean;
  nextDay: boolean;
  sameDayAsArgentina: boolean;
}

export function getVisitorTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Failed to detect visitor timezone:', error);
    return ARGENTINA_TIMEZONE;
  }
}

export function getTimezoneShortName(timezone: string): string {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || '';
  } catch (error) {
    console.error('Failed to get timezone short name:', error);
    return '';
  }
}

export function getTimezoneFriendlyName(timezone: string): string {
  try {
    const cityName = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
    return cityName;
  } catch (error) {
    return timezone;
  }
}

export function convertBusinessHours(): ConvertedBusinessHours {
  const visitorTimezone = getVisitorTimezone();
  const visitorTimezoneShort = getTimezoneShortName(visitorTimezone);

  try {
    const today = new Date();

    const argentinaStartTime = parse(
      `${BUSINESS_START_HOUR}:00`,
      'H:mm',
      today
    );
    const argentinaEndTime = parse(
      `${BUSINESS_END_HOUR}:00`,
      'H:mm',
      today
    );

    const argentinaStartZoned = toZonedTime(argentinaStartTime, ARGENTINA_TIMEZONE);
    const argentinaEndZoned = toZonedTime(argentinaEndTime, ARGENTINA_TIMEZONE);

    const startInVisitorTz = toZonedTime(argentinaStartZoned, visitorTimezone);
    const endInVisitorTz = toZonedTime(argentinaEndZoned, visitorTimezone);

    const visitorStartHour = startInVisitorTz.getHours();
    const visitorEndHour = endInVisitorTz.getHours();
    const visitorStartMinute = startInVisitorTz.getMinutes();
    const visitorEndMinute = endInVisitorTz.getMinutes();

    const spansMultipleDays = visitorEndHour < visitorStartHour ||
                               (visitorEndHour === visitorStartHour && visitorEndMinute < visitorStartMinute);

    const startTime = formatTime(visitorStartHour, visitorStartMinute);
    let endTime = formatTime(visitorEndHour, visitorEndMinute);

    const sameDayAsArgentina = startInVisitorTz.getDate() === argentinaStartZoned.getDate();

    return {
      startTime,
      endTime,
      visitorTimezone,
      visitorTimezoneShort,
      spansMultipleDays,
      nextDay: spansMultipleDays,
      sameDayAsArgentina
    };
  } catch (error) {
    console.error('Error converting business hours:', error);
    return {
      startTime: '9:00 AM',
      endTime: '7:00 PM',
      visitorTimezone: ARGENTINA_TIMEZONE,
      visitorTimezoneShort: 'ART',
      spansMultipleDays: false,
      nextDay: false,
      sameDayAsArgentina: true
    };
  }
}

function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute > 0 ? `:${minute.toString().padStart(2, '0')}` : '';

  return `${displayHour}${displayMinute} ${period}`;
}

export function formatBusinessHoursDisplay(hours: ConvertedBusinessHours): string {
  const isArgentina = hours.visitorTimezone === ARGENTINA_TIMEZONE;

  if (isArgentina) {
    return `Lunes a viernes de ${hours.startTime} a ${hours.endTime}`;
  }

  const timezoneShort = hours.visitorTimezoneShort ? ` (${hours.visitorTimezoneShort})` : '';

  if (hours.spansMultipleDays) {
    return `Lunes a viernes ${hours.startTime} a ${hours.endTime} (día siguiente)${timezoneShort}`;
  }

  return `Lunes a viernes ${hours.startTime} a ${hours.endTime}${timezoneShort}`;
}

export function formatBusinessHoursShort(hours: ConvertedBusinessHours): string {
  if (hours.spansMultipleDays) {
    return `${hours.startTime} - ${hours.endTime} (día siguiente)`;
  }
  return `${hours.startTime} - ${hours.endTime}`;
}

export function isArgentinaTimezone(timezone: string): boolean {
  return timezone === ARGENTINA_TIMEZONE;
}
